import axios from 'axios'
import { getActivePinia } from 'pinia'
import devConfig from '@/config/dev.config'
import authConfig from '@/config/auth.config'
import { useAuthStore } from '@/stores/auth'
import { AppError } from '@/utils/appError'
import {
  loadTokens,
  saveTokens,
  clearTokens,
  isAccessTokenExpired,
  isRefreshTokenExpired,
  shouldRefreshSoon,
  type StoredAuth
} from '@/utils/authStorage'

const AUTH_BYPASS_PATHS = [
  '/api/v1/auth/login',
  '/api/v1/auth/refresh',
  '/api/v1/auth/logout',
  '/api/auth/login',
  '/api/auth/refresh',
  '/api/auth/logout',
  '/api/staff/captcha',
  '/api/staff/send-verification-code',
  '/api/staff/verify-code',
  '/api/staff/reset-password'
]
/**
 * 「业务性 401」路径（T-077 / A6）：这些端点**必须携带登录态**（不能进 AUTH_BYPASS_PATHS，
 * 否则请求拦截器会跳过 Authorization 头 ⇒ 恒 401），但它们的 401 语义是**业务校验失败**
 * 而非会话过期 —— 典型是 `POST /api/v1/auth/change-password` 的「旧密码错误」（后端
 * `services.py:_validate_password_strength` 上游 `change_password` 抛 PermissionError → 401）。
 *
 * 若不特殊处理：401 → 拦截器先 refresh+重试（拿到的仍是 401）→ 落到 `case 401` 的
 * `clearTokens()` ⇒ **用户只是旧密码输错一次，却被登出**，且提示变成「登录已过期，请重新登录」。
 * ⇒ 这一类端点跳过 refresh 重试，并把后端 detail 原样透传（不清 login 态）。
 */
const AUTH_BUSINESS_401_PATHS = ['/api/v1/auth/change-password']

function isAuthBusiness401(config: any): boolean {
  const url = config?.url || ''
  return AUTH_BUSINESS_401_PATHS.some((path) => url.includes(path))
}

let refreshPromise: Promise<{ accessToken: string }> | null = null

function getAuthStoreIfReady() {
  const pinia = getActivePinia()
  if (!pinia) return null
  return useAuthStore()
}

async function requestRefreshToken(refreshToken: string, clientType: string) {
  return http.post('/api/auth/refresh', { refreshToken, clientType }, { headers: { 'X-Skip-Auth': '1' } })
}

/**
 * 统一的 Token 刷新函数
 * 使用 Promise 单例模式避免并发刷新
 * 当 store 可用时优先使用 store 的刷新逻辑，此函数作为降级方案
 */
async function refreshTokenIfNeeded(stored: StoredAuth): Promise<{ accessToken: string } | null> {
  const accessExpired = isAccessTokenExpired(stored.expiresAt)
  const refreshExpired = isRefreshTokenExpired(stored.refreshExpiresAt)
  const shouldRefresh = accessExpired || shouldRefreshSoon(stored.expiresAt, authConfig.refreshThresholdSeconds)

  if (!shouldRefresh) {
    return stored.accessToken ? { accessToken: stored.accessToken } : null
  }

  if (refreshExpired) {
    return null
  }

  if (!refreshPromise) {
    refreshPromise = requestRefreshToken(stored.refreshToken, stored.mode)
      .then((res: any) => {
        const next: StoredAuth = {
          accessToken: res.accessToken,
          refreshToken: res.refreshToken,
          expiresAt: Date.now() + res.expiresIn * 1000,
          refreshExpiresAt: Date.now() + res.refreshExpiresIn * 1000,
          user: res.user,
          scope: stored.scope,
          mode: stored.mode
        }
        saveTokens(next)
        return { accessToken: next.accessToken }
      })
      .finally(() => {
        refreshPromise = null
      })
  }

  return refreshPromise
}

/**
 * 获取有效的 API 基础 URL
 *
 * 逻辑优先级:
 * 1. 如果 enableMock = true (静态开发阶段): 返回空字符串,Mock 会拦截所有请求
 * 2. 如果配置了 apiBaseUrl: 使用配置的地址 (直接请求后端,需要 CORS 支持)
 * 3. 否则: 返回空字符串,使用 vite proxy 代理 (推荐)
 */
function getEffectiveBaseUrl(): string {
  // 静态开发阶段: Mock 会拦截所有请求,不需要 baseURL
  if (devConfig.enableMock) {
    return ''
  }

  // 动态开发阶段: 使用配置的 baseURL 或 vite proxy
  const configuredUrl = devConfig.apiBaseUrl?.trim()

  // 如果配置了 URL,直接使用 (需要后端支持 CORS)
  if (configuredUrl) {
    return configuredUrl
  }

  // 否则使用 vite proxy 代理 (推荐方式)
  return ''
}

const http = axios.create({
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

function isBypassRequest(config: any): boolean {
  const headers = config?.headers
  const skipAuth =
    headers?.['X-Skip-Auth'] ||
    headers?.['x-skip-auth'] ||
    (typeof headers?.get === 'function' ? headers.get('X-Skip-Auth') || headers.get('x-skip-auth') : undefined)

  if (skipAuth && skipAuth !== '0' && skipAuth !== 0 && skipAuth !== false) return true
  const url = config?.url || ''
  return AUTH_BYPASS_PATHS.some((path) => url.includes(path))
}

// Request Interceptor
http.interceptors.request.use(
  async (config) => {
    // Dynamically set baseURL on each request
    config.baseURL = getEffectiveBaseUrl()

    if (isBypassRequest(config)) {
      console.log('[HTTP] Bypass auth for:', config.url)
      return config
    }

    const authStore = getAuthStoreIfReady()
    if (authStore) {
      if (!authStore.initialized) {
        authStore.initAuth()
      }

      if (!authStore.isAuthenticated) {
        return Promise.reject(new AppError('UNAUTHENTICATED', '请先登录', 401, '/login'))
      }

      try {
        await authStore.ensureValidAccessToken()
      } catch {
        return Promise.reject(new AppError('SESSION_EXPIRED', '登录已过期，请重新登录', 401, '/login'))
      }

      if (authStore.accessToken) {
        config.headers.Authorization = `Bearer ${authStore.accessToken}`
      }

      // 租户上下文注入（security-policy.tenantContext）：有当前租户 → X-Tenant-Id
      if (authStore.currentTenantId) {
        config.headers['X-Tenant-Id'] = authStore.currentTenantId
      }

      return config
    }

    // Store 不可用时，使用本地存储的 tokens
    const stored = loadTokens()
    if (!stored) {
      return Promise.reject(new AppError('UNAUTHENTICATED', '请先登录', 401, '/login'))
    }

    const result = await refreshTokenIfNeeded(stored)
    if (!result) {
      clearTokens()
      return Promise.reject(new AppError('SESSION_EXPIRED', '登录已过期，请重新登录', 401, '/login'))
    }

    config.headers.Authorization = `Bearer ${result.accessToken}`
    return config
  },
  (error) => Promise.reject(error)
)

/**
 * 响应错误 → `AppError` 的**纯映射**（无副作用、不触 store / token）—— T-084 抽出以便单测。
 *
 * 🔴 关键契约：本项目的后端错误契约是 `422/404/409 + detail = {code, reason, details[]}`，
 * `details[]` 是**逐条拒绝理由**。原实现把它压进 `message`（对象 → `[object Object]`），
 * 页面就拿不到逐条理由了。故这里**额外**把原始 detail 挂到 `AppError.detail` 上
 * （`message` 语义**保持不变** ⇒ 既有页面零回归）。
 *
 * 401 的会话失效分支（清 token / 跳登录 / refresh 重试）**不在本函数内**——那是拦截器的状态机，
 * 本函数只管"把错误映射成什么"。
 */
export function mapResponseError(args: {
  status: number
  data?: any
  message?: string
  isBusiness401?: boolean
}): AppError {
  const { status, data } = args
  const detail: any = data?.detail
  switch (status) {
    case 401:
      return args.isBusiness401
        ? new AppError('AUTH_FAILED', detail || '认证失败', 401, undefined, detail)
        : new AppError('SESSION_EXPIRED', '登录已过期，请重新登录', 401, '/login')
    case 403:
      // 优先透传后端 detail（如 switch-tenant 的"目标租户不是当前用户岗位租户"）
      return new AppError('FORBIDDEN', detail || 'Permission denied', 403, undefined, detail)
    case 500:
      return new AppError('SERVER_ERROR', 'Server error', 500, undefined, detail)
    default:
      return new AppError(
        'REQUEST_FAILED',
        detail || data?.msg || data?.message || args.message || 'Request failed',
        status,
        undefined,
        detail
      )
  }
}

// Response Interceptor
http.interceptors.response.use(
  (response) => {
    const res = response.data

    if (res && typeof res === 'object' && 'code' in res && 'data' in res) {
      const code = (res as any).code
      if (code === 200 || code === 0) {
        return (res as any).data
      }
      return Promise.reject(
        new AppError('REQUEST_FAILED', (res as any).msg || 'Request failed', response.status)
      )
    }

    return res
  },
  async (error) => {
    if (error.response) {
      const { status } = error.response
      const originalConfig = error.config || {}

      if (
        status === 401 &&
        !isBypassRequest(originalConfig) &&
        !isAuthBusiness401(originalConfig) &&
        !originalConfig._retry
      ) {
        originalConfig._retry = true
        const authStore = getAuthStoreIfReady()
        if (authStore) {
          try {
            await authStore.refreshAccessToken()
            originalConfig.headers = originalConfig.headers || {}
            if (authStore.accessToken) {
              originalConfig.headers.Authorization = `Bearer ${authStore.accessToken}`
            }
            return http(originalConfig)
          } catch {
            await authStore.logout()
            return Promise.reject(new AppError('SESSION_EXPIRED', '登录已过期，请重新登录', 401, '/login'))
          }
        } else {
          // Store 不可用时，使用本地存储的 tokens 进行刷新
          const stored = loadTokens()
          if (!stored) {
            return Promise.reject(new AppError('UNAUTHENTICATED', '请先登录', 401, '/login'))
          }

          const result = await refreshTokenIfNeeded(stored)
          if (!result) {
            clearTokens()
            return Promise.reject(new AppError('SESSION_EXPIRED', '登录已过期，请重新登录', 401, '/login'))
          }

          originalConfig.headers = originalConfig.headers || {}
          originalConfig.headers.Authorization = `Bearer ${result.accessToken}`
          return http(originalConfig)
        }
      }

      switch (status) {
        case 401:
          // 业务性 401（如改密「旧密码错误」）：**不是**会话过期 ⇒ 不清 login 态、透传后端 detail
          if (isAuthBusiness401(originalConfig)) {
            return Promise.reject(
              mapResponseError({ status, data: error.response.data, message: error.message, isBusiness401: true })
            )
          }
          clearTokens()
          // 绕过认证的请求（如登录、刷新 token）401 是业务错误而非会话过期，
          // 直接透传后端 detail 信息
          if (isBypassRequest(originalConfig)) {
            return Promise.reject(
              mapResponseError({ status, data: error.response.data, message: error.message, isBusiness401: true })
            )
          }
          return Promise.reject(mapResponseError({ status, data: error.response.data, message: error.message }))
        case 403:
        case 500:
        default:
          return Promise.reject(
            mapResponseError({ status, data: error.response.data, message: error.message })
          )
      }
    } else {
      return Promise.reject(new AppError('NETWORK_ERROR', 'Network Error'))
    }
  }
)

export default http
